import { IItems, IItemUpdateResult, IItem, PagedItemCollection } from "@pnp/sp/items";
import BaseService from "./base.service";
import { Utilities, String } from "../../utils/utilities";
import { sp, SPRest } from "@pnp/sp";
import { Enums } from '@utils';

export interface ISPListService {
  getItems<T>(request: IGetItemsRequest): Promise<Array<T>>;
  getItem<T>(request: IGetItemRequest): Promise<T>;
  addItem<T>(request: IPostItemRequest<T>): Promise<any>;
  updateItem<T>(request: IPutItemRequest<T>): Promise<IItemUpdateResult>;
  deleteItem(request: IGetItemRequest): Promise<any>;
  addItemsBatch<T>(listName: string, items: any[]): Promise<any>;
  updateItemsBatch<T>(listName: string, items: any[]): Promise<any>;
  deleteItemsBatch<T>(listName: string, items: any[]): Promise<any>;
  getPagedItems<T>(request: IGetItemsRequest): Promise<Array<T>>;
  addItemToLocalStorage(item: any, listName: string): void;
  checkForDuplicatesInLocalStorage(item: any, listName: string): boolean;
  removeExpiredItemsFromLocalStorage(): void;
}

export interface IBatchCreationData{
    item: any;
    result: any;
    error: any;
}
export interface IBaseGetItemRequest {
  listName: string;
  columnsToSelect?: string[];
  expands?: string[];
}
export interface IGetItemsRequest extends IBaseGetItemRequest {
  filter?: string;
  order?: string;
  ascending?: boolean;
  top?: number;
  caching?: boolean;
}
export interface IGetItemRequest extends IBaseGetItemRequest {
  itemId: number;
}

export interface IPostItemRequest<T> extends IBaseGetItemRequest {
  item?: T;
}

export interface IPutItemRequest<T> extends IGetItemRequest {
  item?: T;
}

export default class SPListService extends BaseService
  implements ISPListService {
    

    private localStorageIsEnabledForTable(tableName: string): boolean {
      return Object.values(Enums.LocalStorageTemporaryTables).some((table: any) => table.title === tableName);
    }    

    private verifyAvailabilityForTable(tableName: string): boolean {
      let itemsData = localStorage.getItem(tableName);
      if(itemsData == null) return false

      const itemsDataObject = JSON.parse(itemsData!);
      const currentDate = new Date().getDate();

      if(itemsDataObject.date !== currentDate) return false

      return true
      
    }

    private saveItemsToLocalStorage<T>(listName: string, items: Array<T>): void {
      try {
        const itemData = {
          items: items.map(item => ({...item, __metadata: undefined})),
          date: new Date().getDate()
        };
        const itemsJSON = JSON.stringify(itemData);
        localStorage.setItem(listName, itemsJSON);
      } catch (error) {
        console.error("Error al guardar datos en el localStorage:", error);
      }
    }

    private getListName(listName: string, expands?: string[]): string | null {
      if (Utilities.isNullObject(expands)) {
        return this.verifyAvailabilityForTable(listName) ? listName : null;
      } else {
        const expandedListName = `${listName}_expands`;
        return this.verifyAvailabilityForTable(expandedListName) ? expandedListName : null;
      }
    }

    private getListFromLocalStorage<T>(listName: string): Array<T> | null {

      const storedData = localStorage.getItem(listName);
    
      if (storedData) {
        try {
          const parsedData = JSON.parse(storedData);
          if (Array.isArray(parsedData.items)) {
            return parsedData.items as Array<T>;
          }
        } catch (error) {
          console.error("Error al analizar los datos del localStorage:", error);
        }
      }
        return null;
    }
    
    private getItemsFromLocalStorage<T>(request: IGetItemsRequest): Array<T> | null {
      if (Utilities.isNullObject(request.filter) || request.filter == "") {
        const listName = this.getListName(request.listName, request.expands);
    
        if (listName) {
          const localStorageData = this.getListFromLocalStorage<T>(request.listName);
    
          if (localStorageData) {
            console.log('from Local Storage', listName, localStorageData.length);
            return localStorageData;
          }
        }
      } else {
        const filterCriteria = request.filter.split(' and ');
        const filterObjects = filterCriteria.map(criteria => {
          const match = criteria.match(/(\w+(?:\/\w+)?) (eq|ne) (.+)/);
          if (match) {
            let [, attribute, operator, value] = match;
            return [attribute, operator, value];
          }
          return null;
        });
        if (filterObjects) {
          const listName = this.getListName(request.listName, request.expands);
          if (listName) {
            const localStorageData = this.getListFromLocalStorage<T>(listName);
            if (localStorageData) {
              const filteredItems = localStorageData.filter((item: any) => {
                let valid = true;
                for (let i = 0; i < filterObjects.length; i++) {
                  const filterItem = filterObjects[i];
                  let [attribute, operator, value] = filterItem;
                  const attributeParts = attribute.split('/');
                  const subItemAttribute = attributeParts[0];
                  const subItemField = attributeParts[1];

                  let attributeValue;

                  if (subItemField) {
                    if (item[subItemAttribute] && item[subItemAttribute][subItemField]) {
                      attributeValue = item[subItemAttribute][subItemField];
                    } else {
                      attributeValue = undefined;
                    }
                  } else {
                    attributeValue = item[subItemAttribute];
                  
                  }
                  if (typeof value === "string") {
                    value = value.replace(/'/g, '');
                  }
                  
                  valid = valid && (operator === "eq" && attributeValue == value) || (operator === "ne" && attributeValue != value);
                }

                return valid;
                
              });
    
              if (filteredItems) {
                console.log('from Local Storage with filter', listName, filteredItems.length);
                return filteredItems;
              }
            }
          }
        }
      }
    
      return null;
    }


    public async getListBase(request: IGetItemsRequest): Promise<void> {
      const spContext = await this.getSPContext();
      let items: IItems = spContext.web.lists.getByTitle(request.listName).items;
      const columnsToSelect = Enums.LocalStorageTemporaryTables[request.listName].columns

      let listName = request.listName;

      items = items.select(...columnsToSelect);
      items = items.top(5000);

      if (!Utilities.isNullObject(request.expands)) {
        items = items.expand(...request.expands);
        listName = `${listName}_expands`;
        if (!Utilities.isNullObject(request.columnsToSelect)) {
          items = items.select(...request.columnsToSelect);
        }
      }

      const itemsData = await items.get();
      
      this.saveItemsToLocalStorage(listName, itemsData);

    }
  
  

  public async getItems<T>(request: IGetItemsRequest): Promise<Array<T>> {

    let intento = false;
    if(this.localStorageIsEnabledForTable(request.listName) && Utilities.isNullObject(request.expands)){
      const localStorageData = this.getItemsFromLocalStorage<T>(request);

      if (localStorageData && localStorageData.length > 0) {
        return Promise.resolve(localStorageData);
      }

      console.log('No se pudo obtener la informacion de', request)
      intento = true;
    }
    

    // can't to find in localStorage, search in SP

    const spContext = await this.getSPContext();
    let items: IItems = spContext.web.lists.getByTitle(request.listName).items;

    if (!Utilities.isNullObject(request.filter)) {
      items = items.filter(request.filter);
    }

    if (!Utilities.isNullObject(request.columnsToSelect)) {
      items = items.select(...request.columnsToSelect);
    }

    if (!Utilities.isNullObject(request.expands)) {
      items = items.expand(...request.expands);
    }

    if (!String.IsNullOrWhiteSpace(request.order)) {
      items = items.orderBy(request.order,request.ascending).orderBy("Modified",true);
    }

    if (!Utilities.isNullObject(request.caching) && request.caching) {
      items = items.usingCaching();
    }

    let pagedItems: PagedItemCollection<any[]> = await items.top(request.top ? request.top : 5000).getPaged();

    let allItems = [...pagedItems.results]

    while (pagedItems.hasNext) {
      pagedItems = await pagedItems.getNext();
      allItems = [...allItems, ...pagedItems.results]
    }

    if(this.localStorageIsEnabledForTable(request.listName) && Utilities.isNullObject(request.expands)){
      this.getListBase(request)
    }

    if(intento) console.log("intento", request.listName, "datos sp", allItems.length);

    return allItems;

  }
 
  public async getItem<T>(request: IGetItemRequest): Promise<T> {
    const spContext = await this.getSPContext();
    let item: IItem = spContext.web.lists
      .getByTitle(request.listName)
      .items.getById(request.itemId);

    if (!Utilities.isNullObject(request.columnsToSelect)) {
      item = item.select(...request.columnsToSelect);
    }

    if (!Utilities.isNullObject(request.expands)) {
      item = item.expand(...request.expands);
    }

    return item.get<T>();
  }

  public async getPagedItems<T>(request: IGetItemsRequest): Promise<Array<T>> {

    let intento = false;
    if(this.localStorageIsEnabledForTable(request.listName) && Utilities.isNullObject(request.expands)){
      const localStorageData = this.getItemsFromLocalStorage<T>(request);

      if (localStorageData && localStorageData.length > 0) {
        return Promise.resolve(localStorageData);
      }

      console.log('No se pudo obtener la informacion de', request)
      intento = true;
    }

    const spContext = await this.getSPContext();

    let items: IItems = await spContext.web.lists.getByTitle(request.listName).items;
    let allItems: any = []

    if (!Utilities.isNullObject(request.filter)) {
      items = items.filter(request.filter);
    }

    if (!Utilities.isNullObject(request.columnsToSelect)) {
      items = items.select(...request.columnsToSelect);
    }

    if (!Utilities.isNullObject(request.expands)) {
      items = items.expand(...request.expands);
    }

    if (!String.IsNullOrWhiteSpace(request.order)) {
      items = items.orderBy(request.order, request.ascending);
    }

    let pagedItems: PagedItemCollection<any[]> = await items.top(request.top ? request.top : 5000).getPaged();

    allItems = [...pagedItems.results]

    while (pagedItems.hasNext) {
      pagedItems = await pagedItems.getNext();
      allItems = [...allItems, ...pagedItems.results]
    }

    if(this.localStorageIsEnabledForTable(request.listName) && Utilities.isNullObject(request.expands)){
      this.getListBase(request)
    }

    if(intento) console.log("intento", request.listName, "datos sp", allItems.length);

    return allItems;
  }

  public async addItem<T>(request: IPostItemRequest<T>): Promise<any> {
    this.removeExpiredItemsFromLocalStorage();
    const spContext = await this.getSPContext();
    console.log("%cAñadir", "color: red; font-size: 15px;",request);

    if(this.findDuplicates(request.listName)) {
      let duplicate: any = this.checkForDuplicatesInLocalStorage(request.item, request.listName)
    
      if(!!duplicate) {
        console.log("%cDuplicado", "color: red; font-size: 15px;",request);
        setTimeout(() => {
          Utilities.notifyWarning('Para realizar el guardado, cierre pestañas y vuelva a intentarlo.'); 
        },5000)
        return request.item;
      }

    }

    const response = await spContext.web.lists
              .getByTitle(request.listName)
              .items.add(request.item);
    this.addItemToLocalStorage(request.item,request.listName);
    return response.item.get();
    
  }

  public async updateItem<T>(
    request: IPutItemRequest<T>
  ): Promise<IItemUpdateResult> {
    try {
      console.log("actualizacion",request);
    const spContext = await this.getSPContext();
    const response = await spContext.web.lists
      .getByTitle(request.listName)
      .items.getById(request.itemId)
      .update(request.item)
    return response.item.get();
      } catch (e) {
      console.log('Error',e)
      }
   
  }

  public async deleteItem(request: IGetItemRequest): Promise<any> {
    const spContext = await this.getSPContext();
    const response = spContext.web.lists
      .getByTitle(request.listName)
      .items.getById(request.itemId)
      .delete();
    return response;
  }

  public async addItemsBatch<T>(listName: string, items: any[]): Promise<any>{
    this.removeExpiredItemsFromLocalStorage();
    const spContext = await this.getSPContext();
    const filteredItems = this.filterItemsById(items);
    if(filteredItems.length > 0) console.log("%cAñadir items %c" + listName, "color: red; font-size: 15px;", "color: red; font-size: 15px;", filteredItems);
    const list = spContext.web.lists.getByTitle(listName);
    const entityTypeFullName = await list.getListItemEntityTypeFullName();

    if(this.findDuplicates(listName)) {
      if(items.length > 0) this.searchItemsBatchDuplicates(items, listName);
      if(items.length > 0) this.saveItemsBatchDuplicates(items, listName);
    }
    
    return this.ProcessBatch(list, entityTypeFullName, filteredItems, 0, false, false, spContext, []);
  }

  public async updateItemsBatch<T>(listName: string, items: any[]): Promise<any>{
    const spContext = await this.getSPContext();
    if(items.length > 0) console.log("actualizar items",listName,items);
    const list = spContext.web.lists.getByTitle(listName);
    const entityTypeFullName = await list.getListItemEntityTypeFullName();
    return this.ProcessBatch(list, entityTypeFullName, items, 0, true, false, spContext, []);
  }

  public async deleteItemsBatch<T>(listName: string, items: any[]): Promise<any>{
    const spContext = await this.getSPContext();
    const list = spContext.web.lists.getByTitle(listName);    
    const entityTypeFullName = await list.getListItemEntityTypeFullName();  
    return this.ProcessBatch(list, entityTypeFullName, items, 0, false, true, spContext);
  }

  private async ProcessBatch(list: any, itemEntityType: string | undefined, data: any[], index:number, isUpdate?:boolean, isDelete?:boolean, spContext?:SPRest, added?:any[]):Promise<IBatchCreationData[]>{
    const bathcSize = data.length < 25 ? data.length : 25;
    const len = index + bathcSize;
    
    if(data.length > index ){
      const batch = spContext.web.createBatch();  
      const success = (res:any) => { if (!isDelete) added.push(res); }
      const error = (err:any) => { console.log(err) }         
      for (let i = index; i < len && i < data.length; i++){
        const itemData: IBatchCreationData = {
          item: data[i],
          result: success,
          error: error
        };
        if(!isDelete){
          if(isUpdate){
            list.items.getById(itemData.item.Id).inBatch(batch).update(itemData.item, "*", itemEntityType).then(success).catch(error);
          }else{
            list.items.inBatch(batch).add(itemData.item, itemEntityType).then(success).catch(error);
          }
        }else{
          list.items.getById(itemData.item.Id).inBatch(batch).delete().then(success).catch(error);
        }
      }

      await batch.execute();
      return await this.ProcessBatch(list, itemEntityType, data, len, isUpdate, isDelete, spContext, added);
    }else{
      return new Promise(r => r(added));
    }       
  }

  private findDuplicates(listName: string) : boolean {
    if((listName.includes("Seguimiento") || listName.includes("Proa")) && !listName.includes("Version")) {
      return true
    }
    return false
  }

  public filterItemsById(items: any[]): any[] {
    return items.filter(item => {
      const keys = Object.keys(item);
      for (const key of keys) {
        if (key.toLowerCase().endsWith('id') && key !== 'Id') {
          if(item[key] !== 0) return true
          else return false
        }
      }
      return true;
    });
  }

  public searchItemsBatchDuplicates(items: any[], listName: string): any {
    let foundDuplicates = false

    items.forEach(item => {
      let aux = this.checkForDuplicatesInLocalStorage(item, listName);
      if(aux !== false) {
        foundDuplicates = true
      }
    });

    if(foundDuplicates) window.location.reload()

  }

  public saveItemsBatchDuplicates(items: any[], listName: string): any {
    items.forEach((item, index) => {
      this.addItemToLocalStorage(item, listName, true, index);
    });
  }

  private formatItemUndefinedEntries(item: any) {
    const auxItem = {...item}
    for (const key in auxItem) {
      if (auxItem.hasOwnProperty(key) && auxItem[key] === undefined) {
        auxItem[key] = null;
      }
    }

    return auxItem
  }
  

  public addItemToLocalStorage(item: any, listName: string, batch: boolean = false, index?: number): void {
    item = this.formatItemUndefinedEntries(item);
    
    const key = (!batch) ? `item_${listName}_${new Date().toISOString()}` : `item_${listName}_${new Date().toISOString()}_${index.toString()}`;
    const itemData = {
      item: item,
      timestamp: new Date().getTime()
    };
    localStorage.setItem(key, JSON.stringify(itemData));
  }

  public checkForDuplicatesInLocalStorage(item: any, listName: string): any {
    const localStorageKeys = Object.keys(localStorage);
    const duplicates = localStorageKeys.filter(key =>
      key.startsWith(`item_${listName}`)
    );

    for (const key of duplicates) {
      const itemData = JSON.parse(localStorage.getItem(key)!);
      if (this.areItemsEqual(this.formatItemUndefinedEntries(item), itemData.item)) {
        return key;
      }
    }

    return false;
  }

  public removeExpiredItemsFromLocalStorage(): void {
    const localStorageKeys = Object.keys(localStorage);
    const currentTime = new Date().getTime();

    for (const key of localStorageKeys) {
      const itemData = JSON.parse(localStorage.getItem(key)!);
      const itemTimestamp = itemData.timestamp;
      const timeDifference = currentTime - itemTimestamp;
      const expirationTime = 60*1000*60 ; // 1 hour in milliseconds 60*60*100

      if (timeDifference >= expirationTime) {
        localStorage.removeItem(key);
      }
    }
  }

  private areItemsEqual(item1: any, item2: any): boolean {

    if (item1 === item2) {
      return true;
    }
  
    if (
      typeof item1 !== 'object' ||
      typeof item2 !== 'object' ||
      item1 === null ||
      item2 === null
    ) {
      return false; 
    }
  
    const keys1 = Object.keys(item1);
    const keys2 = Object.keys(item2);
  
    if (keys1.length !== keys2.length) {
      return false; 
    }
  
    for (const key of keys1) {
      if (key === 'id' || key === 'Id') {
        continue;
      }
  
      if (!item2.hasOwnProperty(key) || item1[key] !== item2[key]) {
        return false;
      }
    }
  
    return true;
  }
}
